import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import collectionRepository from '@/features/collections/repositories/CollectionRepository';
import { useCollectionStore } from '@/features/collections/stores/useCollectionStore';
import { CollectionFilter, type Collection } from '@/features/collections/types/collection.types';
import { fetchCollectionsByFilter } from '@/features/collections/hooks/useCollectionsQuery';
import {
  collectionKeys,
  invalidateCollectionQueries,
  invalidatePageQueries,
} from '@/shared/query/queryKeys';

export function useCollectionListActions() {
  const queryClient = useQueryClient();
  const currentCollection = useCollectionStore(state => state.currentCollection);
  const updateCollectionInStore = useCollectionStore(state => state.updateCollection);

  const loadCollections = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
  }, [queryClient]);

  const addCollection = useCallback(async () => {
    const collection = await collectionRepository.addCollection();
    void invalidateCollectionQueries(queryClient);
    void invalidatePageQueries(queryClient);
    return collection;
  }, [queryClient]);

  const archiveCollection = useCallback(
    async (collection: Collection) => {
      await collectionRepository.archiveCollectionData(collection);
      await invalidateCollectionQueries(queryClient);
      await invalidatePageQueries(queryClient);
      await loadCollections();
    },
    [loadCollections, queryClient]
  );

  const unarchiveCollection = useCallback(
    async (collection: Collection) => {
      if (!collection.archivedAt) return;

      await collectionRepository.unarchiveCollectionData(collection);
      await invalidateCollectionQueries(queryClient);
      await invalidatePageQueries(queryClient);
      await loadCollections();
    },
    [loadCollections, queryClient]
  );

  const searchCollections = useCallback(
    (query: string) =>
      queryClient.fetchQuery({
        queryKey: collectionKeys.search(query),
        queryFn: () => collectionRepository.searchCollections(query),
      }),
    [queryClient]
  );

  const updateCollection = useCallback(
    async (id: string, collection: Partial<Collection>) => {
      await collectionRepository.updateCollection(id, collection);
      updateCollectionInStore(id, collection);
      void queryClient.invalidateQueries({ queryKey: collectionKeys.detail(id) });
      void invalidateCollectionQueries(queryClient);
    },
    [queryClient, updateCollectionInStore]
  );

  const toggleProperty = useCallback(
    async (id: string, property: 'isFavourite' | 'isPublic') => {
      await collectionRepository.toggleProperty(id, property);

      if (currentCollection?.id === id) {
        updateCollectionInStore(id, {
          [property]: !currentCollection[property],
        });
      }

      await invalidateCollectionQueries(queryClient);
      await loadCollections();
    },
    [currentCollection, loadCollections, queryClient, updateCollectionInStore]
  );

  const getCollections = useCallback(
    (collectionFilter: CollectionFilter, tagFilter?: string) =>
      queryClient.fetchQuery({
        queryKey: collectionKeys.list(collectionFilter, tagFilter),
        queryFn: () => fetchCollectionsByFilter(collectionFilter, tagFilter),
      }),
    [queryClient]
  );

  return {
    loadCollections,
    addCollection,
    archiveCollection,
    unarchiveCollection,
    searchCollections,
    updateCollection,
    toggleProperty,
    getCollections,
  };
}
